import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useContent } from '../../context/ContentContext';

const Icon = ({ children, className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);

function Navbar() {
  const cartRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const { cart } = useCart();
  const { user, logout } = useAuth();
  const { content, loading } = useContent();
  const navigate = useNavigate();
  const location = useLocation();

  const itemCount = cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const API_BASE = process.env.REACT_APP_IMAGE_URL || process.env.REACT_APP_API_BASE || '';
  const brandLogo = content?.settings?.brand_logo
    ? `${API_BASE}${content.settings.brand_logo.startsWith('/') ? '' : '/'}${content.settings.brand_logo}`
    : null;
  const brandName = content?.settings?.brand_name || 'LewiEat';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setSearchOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (itemCount && cartRef.current) {
      gsap.fromTo(cartRef.current, { scale: 0.72 }, { scale: 1, duration: 0.42, ease: 'back.out(2)' });
    }
  }, [itemCount]);

  const submitSearch = (event) => {
    event.preventDefault();
    const value = searchQuery.trim();
    if (!value) return;
    navigate(`/restaurants?search=${encodeURIComponent(value)}`);
    setSearchOpen(false);
  };

  const signOut = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const navLinks = [
    { to: '/restaurants', label: 'Restaurants' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
    { to: '/sell-with-us', label: 'Sell with us' },
  ];

  return (
    <header className={`sticky top-0 z-50 border-b transition duration-300 ${scrolled ? 'border-black/10 bg-white/92 shadow-[0_8px_30px_rgba(42,25,20,.08)] backdrop-blur-xl' : 'border-black/5 bg-[#fbfaf7]/95 backdrop-blur-lg'}`}>
      <div className="mx-auto flex h-[68px] max-w-7xl items-center gap-3 px-4 sm:h-20 sm:px-6 lg:px-8">
        <Link to="/" className="flex min-w-0 flex-1 items-center gap-2.5 lg:flex-none" aria-label={`${brandName} home`}>
          {loading ? (
            <span className="h-9 w-28 animate-pulse rounded-xl bg-black/5" />
          ) : brandLogo ? (
            <img src={brandLogo} alt={brandName} className="h-10 max-w-[145px] object-contain sm:h-12 sm:max-w-[180px]" />
          ) : (
            <span className="truncate font-playfair-display text-2xl font-black tracking-tight text-primary sm:text-3xl">{brandName}</span>
          )}
        </Link>

        <nav className="mx-auto hidden items-center gap-8 lg:flex" aria-label="Primary navigation">
          {navLinks.map((link) => {
            const active = location.pathname === link.to;
            return <Link key={link.to} to={link.to} className={`relative py-2 text-sm font-bold transition ${active ? 'text-primary' : 'text-secondary/75 hover:text-primary'}`}>{link.label}{active && <span className="absolute inset-x-2 -bottom-1 h-0.5 rounded-full bg-primary" />}</Link>;
          })}
        </nav>

        <form onSubmit={submitSearch} className="hidden w-64 items-center rounded-full border border-black/10 bg-white px-4 shadow-sm focus-within:border-primary/40 focus-within:ring-4 focus-within:ring-primary/10 xl:flex">
          <Icon className="h-4 w-4 text-gray-400"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></Icon>
          <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search restaurants" className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-sm outline-none" />
        </form>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button type="button" onClick={() => setSearchOpen((value) => !value)} className="app-icon-button xl:hidden" aria-label="Search restaurants" aria-expanded={searchOpen}>
            <Icon><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></Icon>
          </button>

          <Link to="/cart" className="app-icon-button relative hidden sm:grid" aria-label={`Cart with ${itemCount} items`}>
            <Icon><path d="M4 5h2l2 10h9l2-7H7"/><circle cx="10" cy="19" r="1"/><circle cx="17" cy="19" r="1"/></Icon>
            {itemCount > 0 && <span ref={cartRef} className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-black text-white shadow">{itemCount > 99 ? '99+' : itemCount}</span>}
          </Link>

          <div className="relative hidden sm:block">
            <button type="button" onClick={() => setAccountOpen((value) => !value)} className="app-icon-button" aria-label={user ? 'Open account menu' : 'Sign in'} aria-expanded={accountOpen}>
              <Icon><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></Icon>
            </button>
            {accountOpen && <div className="absolute right-0 top-12 w-52 overflow-hidden rounded-2xl border border-black/5 bg-white p-2 shadow-[0_20px_60px_rgba(30,18,14,.18)]">{user ? <><p className="truncate px-3 py-2 text-xs font-bold text-gray-400">{user.name}</p><Link to="/dashboard" className="block rounded-xl px-3 py-2.5 text-sm font-semibold hover:bg-primary/5">My account</Link><button onClick={signOut} className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-red-600 hover:bg-red-50">Sign out</button></> : <><Link to="/login" className="block rounded-xl px-3 py-2.5 text-sm font-semibold hover:bg-primary/5">Sign in</Link><Link to="/register" className="block rounded-xl bg-primary px-3 py-2.5 text-center text-sm font-bold text-white">Create account</Link></>}</div>}
          </div>
        </div>
      </div>

      {searchOpen && <form onSubmit={submitSearch} className="border-t border-black/5 bg-white px-4 py-3 xl:hidden"><div className="mx-auto flex max-w-2xl items-center gap-2 rounded-2xl bg-[#f5f1ed] px-4"><Icon className="h-5 w-5 text-gray-400"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></Icon><input autoFocus value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="What do you feel like eating?" className="min-w-0 flex-1 bg-transparent py-3.5 text-base outline-none"/><button className="text-sm font-black text-primary">Search</button></div></form>}
    </header>
  );
}

export default Navbar;
